import React, { FC, useEffect, useMemo, useState } from 'react';
import ContentHeader from '../../components/Admin/ContentHeader';
import BranchQueueFilter from '../../components/Admin/BranchQueueFilter/BranchQueueFilter';
import classes from './WorkplacesPage.module.scss';
import { ModalWorkplace } from '../../components/Admin/Modals/ModalWorkplace/ModalWorkplace';
import WorkingPlacesTable, {
  workingPlaceType
} from '../../components/Admin/Tables/WorkingPlacesTable/WorkingPlacesTable';
import { useGetAllWorkingPlaces } from '../../hooks/Admin/WorkingPlacesTable/useGetAllWorkingPlaces';
import { useDeleteWorkingPlace } from '../../hooks/Admin/WorkingPlacesTable/useDeleteWorkingPlace';
import { usePutWorkingPlaceStatus } from '../../hooks/Admin/WorkingPlacesTable/usePutWorkingPlaceStatus';
import { useGetAllSystemTopics } from '../../hooks/Admin/WorkingPlacesTable/useGetAllSystemTopics';
import { ModalAccessLevels } from '../../components/Admin/Modals/ModalWorkplace/ModalAssignServices';
import { useEditWorkingPlace } from '../../hooks/Admin/WorkingPlacesTable/useEditWorkingPlace';
import { useCreateWorkingPlace } from '../../hooks/Admin/WorkingPlacesTable/useCreateWorkingPlace';
import { useMyPermissions } from '../../hooks/useMyPermissions';
import { useAdminQueueIdsFilter } from '../../hooks/Admin/useAdminQueueIdsFilter';

const tableName = 'Рабочие места';

export type RequestBodyType = {
  id: number;
  requestBody: {
    type: string;
    description: string;
    officeNumber?: number;
    windowNumber?: number;
    digitalDisplayIp?: string;
    users: number[] | [];
    topics: number[] | [];
  };
};

export type WorkPlaceType = {
  id?: number;
  description: string;
  windowNumber: number;
  officeNumber: number;
  type: string;
  assignedEmployees: AssignedEmployeeType[] | [];
  assignedEmployeesIds: number[] | [];
  digitalDisplayIp: string;
  queueName?: string | null;
  branchIds?: number[];
  queueIds?: number[];
};

export type AssignedServicesType = {
  id: number;
  nameRu: string;
  branchIds?: number[];
  queueIds?: number[];
};

export type AssignedEmployeeType = {
  id: number;
  name: string;
  role: string;
};

function filterWorkingPlacesByBranchQueue(
  places: workingPlaceType[],
  branchIds: number[],
  queueIds: number[]
): workingPlaceType[] {
  if (branchIds.length === 0 && queueIds.length === 0) return places;
  return places.filter((row) => {
    const branchMatch =
      branchIds.length === 0 ||
      (row.branches?.length && row.branches.some((b) => branchIds.includes(b.id)));
    const queueMatch =
      queueIds.length === 0 ||
      (row.queues?.length && row.queues.some((q) => queueIds.includes(q.id)));
    return Boolean(branchMatch && queueMatch);
  });
}

const WorkplacesPage: FC = () => {
  const { canView, canEdit: canEditPermission } = useMyPermissions();
  const canEdit = canEditPermission("WORKPLACES_EDIT");
  const canDelete = canEditPermission("WORKPLACES_DELETE");
  const canChangeActivity = canEditPermission("WORKPLACES_ACTIVITY");
  const canCreate = canView("WORKPLACES_CREATE");

  const { queueIdsFilter } = useAdminQueueIdsFilter();
  const [modalActive, setModalActive] = useState(false);
  const [modalAccessLevelsActive, setModalAccessLevelsActive] = useState(false);
  const [deleteModalActive, setDeleteModalActive] = useState(false);
  const [filterBranchIds, setFilterBranchIds] = useState<number[]>([]);
  const [filterQueueIds, setFilterQueueIds] = useState<number[]>([]);
  const [activeRowData, setActiveRowData] = useState<workingPlaceType>(null);
  const [assignedServicesList, setAssignedServicesList] = useState<AssignedServicesType[]>([]);
  const [availableServicesList, setAvailableServicesList] = useState<AssignedServicesType[]>([]);
  const [agentsRequestData, setAgentsRequestData] = useState<WorkPlaceType | null>(null);
  const [backStage, setBackStage] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const { workingPlacesResp, isLoadingWorkingPlaces } = useGetAllWorkingPlaces();
  const filteredWorkingPlaces = useMemo(
    () =>
      filterWorkingPlacesByBranchQueue(
        workingPlacesResp ?? [],
        filterBranchIds,
        filterQueueIds
      ),
    [workingPlacesResp, filterBranchIds, filterQueueIds]
  );

  const isNoWorkingPlacesForSelectedQueue =
    !isLoadingWorkingPlaces &&
    (filteredWorkingPlaces?.length ?? 0) === 0 &&
    filterQueueIds.length > 0;

  const { deleteWorkingPlace, isDeleteWorkingPlaceLoading } =
    useDeleteWorkingPlace(setDeleteModalActive);
  const { updateWorkingPalceStatus } = usePutWorkingPlaceStatus();
  const {
    allSystemTopics,
    isLoadingAllSystemTopics,
    isErrorAllSystemTopics
  } = useGetAllSystemTopics();
  const { editWorkingPlace, isLoadingEditWorkingPlace } = useEditWorkingPlace(
    setModalAccessLevelsActive,
    setAgentsRequestData
  );
  const { createWorkingPlace, isLoadingCreateWorkingPlace } = useCreateWorkingPlace(
    setModalAccessLevelsActive,
    setAgentsRequestData,
    setIsCreateMode
  );
  const handleActiveModalOnCreateBtn = (): void => {
    setActiveRowData(null);
    setIsCreateMode(true);
    setModalActive(true);
  };

  const handleActiveModalOnEditBtn = (): void => {
    setIsCreateMode(false);
    setModalActive(true);
  };

  useEffect(() => {
    if (activeRowData && allSystemTopics?.length) {
      setAssignedServicesList(activeRowData.assignedServices);
      setAvailableServicesList(allSystemTopics);
    }
  }, [activeRowData, allSystemTopics]);

  useEffect(() => {
    if (isCreateMode && allSystemTopics?.length) {
      setAvailableServicesList(allSystemTopics);
      setAssignedServicesList([]);
    }
  }, [isCreateMode, allSystemTopics]);

  return (
    <div className={classes.container}>
      <ContentHeader
        name={tableName}
        handleButtonClick={canCreate ? handleActiveModalOnCreateBtn : undefined}
        filterContent={
          <BranchQueueFilter
            selectedBranchIds={filterBranchIds}
            selectedQueueIds={filterQueueIds}
            onBranchChange={setFilterBranchIds}
            onQueueChange={setFilterQueueIds}
            queueIdsFilter={queueIdsFilter}
          />
        }
      />
      {modalActive && (
        <ModalWorkplace
          isActive={modalActive}
          isCreateMode={isCreateMode}
          setIsCreateMode={setIsCreateMode}
          setActive={setModalActive}
          allWorkingPlaces={workingPlacesResp ?? []}
          data={activeRowData}
          setActiveRowData={setActiveRowData}
          setAgentsRequestData={setAgentsRequestData}
          setModalAccessLevelsActive={setModalAccessLevelsActive}
          agentsRequestData={agentsRequestData}
          backStage={backStage}
          setBackStage={setBackStage}
        />
      )}
      {!isNoWorkingPlacesForSelectedQueue ? (
        <WorkingPlacesTable
          setActiveRowData={setActiveRowData}
          handleActiveModalOnEditBtn={handleActiveModalOnEditBtn}
          workingPlacesData={filteredWorkingPlaces}
          deleteWorkingPlace={deleteWorkingPlace}
          updateWorkingPalceStatus={updateWorkingPalceStatus}
          isDeleteWorkingPlaceLoading={isDeleteWorkingPlaceLoading}
          deleteModalActive={deleteModalActive}
          setDeleteModalActive={setDeleteModalActive}
          activeRowData={activeRowData}
          isLoadingData={isLoadingWorkingPlaces}
          canEdit={canEdit}
          canDelete={canDelete}
          canChangeActivity={canChangeActivity}
        />
      ) : (
        <div className={classes.noWorkingPlacesContainer}>
          <div className={classes.noWorkingPlacesText}>Нет рабочих мест</div>
        </div>
      )}
      {modalAccessLevelsActive && (
        <ModalAccessLevels
          isActive={modalAccessLevelsActive}
          setActive={setModalAccessLevelsActive}
          assignedServices={assignedServicesList}
          setAssignedServices={setAssignedServicesList}
          availableServices={availableServicesList}
          setAvailableServices={setAvailableServicesList}
          setActiveRowData={setActiveRowData}
          allSystemTopics={allSystemTopics}
          setBackStage={setBackStage}
          setAgentsRequestData={setAgentsRequestData}
          isCreateMode={isCreateMode}
          setIsCreateMode={setIsCreateMode}
          setModalActive={setModalActive}
          createWorkingPlace={createWorkingPlace}
          editWorkingPlace={editWorkingPlace}
          isLoadingCreateWorkingPlace={isLoadingCreateWorkingPlace || isLoadingAllSystemTopics}
          isLoadingEditWorkingPlace={isLoadingEditWorkingPlace || isLoadingAllSystemTopics}
          agentsRequestData={agentsRequestData}
          isTopicsForbidden={isErrorAllSystemTopics}
        />
      )}
    </div>
  );
};

export default WorkplacesPage;
